import React from "react";
import { Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PeopleIcon from "@mui/icons-material/People";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import SellIcon from "@mui/icons-material/Sell";
import MapIcon from "@mui/icons-material/Map";
import HubIcon from "@mui/icons-material/Hub";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import SidebarBrand from "./SidebarBrand";
import { useAuth } from "../auth/AuthContext";

const NAV_GROUPS = [
    {
        label: "Workspace",
        items: [{ path: "/", label: "Dashboard", icon: DashboardIcon }],
    },
    {
        label: "Operations",
        items: [
            { path: "/tasks", label: "Tasks", icon: AssignmentIcon },
            { path: "/calendar", label: "Calendar", icon: CalendarMonthIcon },
            { path: "/management", label: "Management", icon: AdminPanelSettingsIcon },
            { path: "/users", label: "User access", icon: PeopleIcon, admin: true },
            { path: "/software-licenses", label: "Software licenses", icon: VpnKeyIcon, admin: true },
            { path: "/software-installers", label: "App installers", icon: Inventory2Icon, admin: true },
        ],
    },
    {
        label: "Pipeline",
        items: [
            { path: "/customers", label: "Customers", icon: PeopleIcon },
            { path: "/notes", label: "Notes", icon: StickyNote2Icon },
            { path: "/quotes", label: "Quotes", icon: RequestQuoteIcon },
            { path: "/planner", label: "Planner", icon: AccountTreeIcon },
            { path: "/job-actions", label: "Job Actions", icon: MoreTimeIcon },
            { path: "/pricing", label: "Quote Settings", icon: SellIcon },
            { path: "/finances", label: "Finances", icon: AccountBalanceWalletIcon },
        ],
    },
    {
        label: "Direction",
        items: [
            { path: "/roadmap", label: "Roadmap", icon: MapIcon },
            { path: "/systemsmap", label: "Systems Map", icon: HubIcon },
        ],
    },
];

const Sidebar = ({ mobileOpen = false, onMobileClose = () => { } }) => {
    const location = useLocation();
    const { user } = useAuth();

    const navigation = (
        <>
            <Box sx={{ px: 2, mb: 2 }}>
                <SidebarBrand />
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    px: 1,
                    pb: "calc(16px + env(safe-area-inset-bottom))",
                    WebkitOverflowScrolling: "touch",
                    overscrollBehavior: "contain",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                {NAV_GROUPS.map((group) => (
                    <Box key={group.label} sx={{ mb: 2 }}>
                        <Typography
                            variant="overline"
                            sx={{
                                px: 1.5,
                                mb: 0.75,
                                display: "block",
                                color: "text.secondary",
                                letterSpacing: "0.14em",
                                fontWeight: 800,
                                fontSize: "0.65rem",
                            }}
                        >
                            {group.label}
                        </Typography>
                        <List disablePadding>
                            {group.items.filter((link) => !link.admin || user?.role === "admin").map((link) => {
                                const selected = location.pathname === link.path;
                                const Icon = link.icon;
                                return (
                                    <ListItem key={link.path} disablePadding sx={{ mb: 0.35 }}>
                                        <ListItemButton component={Link} to={link.path} selected={selected} onClick={onMobileClose} sx={{ borderRadius: 2, py: 1 }}>
                                            <ListItemIcon sx={{ minWidth: 38, color: selected ? "primary.main" : "text.secondary" }}>
                                                <Icon sx={{ fontSize: 20 }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={link.label}
                                                slotProps={{
                                                    primary: {
                                                        fontSize: "0.9rem",
                                                        fontWeight: selected ? 700 : 500,
                                                    },
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Box>
                ))}
            </Box>
        </>
    );

    const sidebarSx = (theme) => ({
        width: 248,
        flexShrink: 0,
        alignSelf: "stretch",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        py: 2,
        overflow: "hidden",
        borderRight: 1,
        borderColor: "divider",
        backgroundColor: theme.palette.background.paper,
        backdropFilter: "none",
    });

    return (
        <>
            <Box component="nav" className="app-sidebar" sx={(theme) => ({
                ...sidebarSx(theme),
                display: { xs: "none", md: "flex" },
            })}>
                {navigation}
            </Box>
            <Drawer
                anchor="left"
                open={mobileOpen}
                onClose={onMobileClose}
                ModalProps={{ keepMounted: true }}
                sx={{ display: { xs: "block", md: "none" } }}
                PaperProps={{
                    sx: (theme) => ({
                        ...sidebarSx(theme),
                        width: 280,
                        height: "100%",
                        maxHeight: "100dvh",
                    }),
                }}
            >
                {navigation}
            </Drawer>
        </>
    );
};

export default Sidebar;
